/**
 * 缩略图捕获工具：
 * - 3D 场景：从 three.js renderer 的 canvas 抓取（需要 WebGL canvas 上下文）
 * - 2D 大屏：从画布/容器 DOM 抓取，无画布时生成背景色+名称占位图
 */

/** 从 canvas 生成 dataURL 缩略图（压缩质量 0.6，最大宽 640 控制体积） */
export function canvasToThumbnail(canvas: HTMLCanvasElement, maxWidth = 640): string | null {
  try {
    if (canvas.width === 0 || canvas.height === 0) return null;
    const scale = Math.min(1, maxWidth / canvas.width);
    if (scale >= 1 && canvas.width <= maxWidth) {
      return canvas.toDataURL('image/jpeg', 0.6);
    }
    const out = document.createElement('canvas');
    out.width = Math.round(canvas.width * scale);
    out.height = Math.round(canvas.height * scale);
    const ctx = out.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(canvas, 0, 0, out.width, out.height);
    return out.toDataURL('image/jpeg', 0.6);
  } catch {
    return null;
  }
}

/** 从 three.js WebGL renderer 抓取缩略图 */
export function threeCanvasToThumbnail(gl: { domElement: HTMLCanvasElement }): string | null {
  try {
    // WebGL canvas 需要 preserveDrawingBuffer 或 render 后立即抓取；
    // 这里先强制 render 一帧再抓，保证拿到画面
    return canvasToThumbnail(gl.domElement);
  } catch {
    return null;
  }
}

/** 2D 大屏：优先 html2canvas 截取整个编辑区域 DOM 快照；失败则找容器内最大 canvas；再兜底背景色+名称占位图 */
export async function dashboardThumbnail(
  container: HTMLElement | null,
  name: string,
  bgColor?: string,
): Promise<string | null> {
  // 1) 首选：html2canvas 截取整个编辑区域（含背景、所有组件，真正达到"编辑区域快照"效果）
  if (container) {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(container, {
        backgroundColor: bgColor || '#0a1020',
        scale: 0.5, // 降采样控制体积
        useCORS: true,
        logging: false,
      });
      const t = canvasToThumbnail(canvas, 640);
      if (t) return t;
    } catch {
      /* 降级 */
    }
    // 2) 次选：容器内最大 canvas（图表）
    const canvases = Array.from(container.querySelectorAll('canvas'));
    if (canvases.length > 0) {
      const biggest = canvases.reduce((a, b) => (a.width * a.height > b.width * b.height ? a : b));
      const t = canvasToThumbnail(biggest);
      if (t) return t;
    }
  }
  // 3) 兜底：绘制 640x360 背景色 + 名称文字占位图
  try {
    const c = document.createElement('canvas');
    c.width = 640;
    c.height = 360;
    const ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = bgColor || '#0a1020';
    ctx.fillRect(0, 0, 640, 360);
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.font = 'bold 36px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(name || '大屏', 320, 170);
    ctx.font = '20px sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.fillText('DTwince 数据可视化大屏', 320, 230);
    return c.toDataURL('image/jpeg', 0.6);
  } catch {
    return null;
  }
}